import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowUpDown, Plus } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { ActiveIssuesChart } from '../../components/charts/ActiveIssuesChart'
import { useTickets } from '../../hooks/api/useTickets'
import { LoadingState } from '../../components/ui/LoadingState'
import { ErrorState } from '../../components/ui/ErrorState'
import { Select } from '../../components/ui/Select'
import { FilterDropdown, FilterState } from '../../components/ui/FilterDropdown'
import { AddDeviceModal } from '../../components/modals/AddDeviceModal'
import { useCreateDevice } from '../../hooks/api/useDevices'
import type { DeviceFormData } from '../../components/modals/AddDeviceModal'

type DateRangeFilter = '7d' | '30d' | '90d' | 'all'

const dateRangeOptions: { label: string; value: DateRangeFilter; days?: number }[] = [
  { label: 'Last 7 days', value: '7d', days: 7 },
  { label: 'Last 30 days', value: '30d', days: 30 },
  { label: 'Last 90 days', value: '90d', days: 90 },
  { label: 'All time', value: 'all' }
]

export const AdminDashboardPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { data: tickets = [], isLoading: ticketsLoading } = useTickets()

  const [filters, setFilters] = useState<FilterState>({
    organization: null,
    status: null,
    priority: null
  })
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>('all')
  const [sortDescending, setSortDescending] = useState(true)
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
  const createDeviceMutation = useCreateDevice()

  const organizationOptions = useMemo(() => {
    const unique = new Set<string>()
    tickets.forEach(ticket => {
      if (ticket.company_name) {
        unique.add(ticket.company_name)
      }
    })
    return Array.from(unique).sort()
  }, [tickets])

  const filteredTickets = useMemo(() => {
    const now = new Date()
    return [...tickets]
      .filter(ticket => {
        if (filters.organization && ticket.company_name !== filters.organization) return false
        if (filters.priority && ticket.priority !== filters.priority) return false
        if (filters.status && ticket.status !== filters.status) return false

        if (dateRangeFilter !== 'all' && ticket.created_at) {
          const option = dateRangeOptions.find(opt => opt.value === dateRangeFilter)
          if (option?.days) {
            const threshold = new Date(now)
            threshold.setDate(now.getDate() - option.days)
            const createdDate = new Date(ticket.created_at)
            if (createdDate < threshold) return false
          }
        }

        return true
      })
      .sort((a, b) => {
        const aDate = new Date(a.created_at).getTime()
        const bDate = new Date(b.created_at).getTime()
        return sortDescending ? bDate - aDate : aDate - bDate
      })
  }, [tickets, filters, dateRangeFilter, sortDescending])

  if (isLoading || ticketsLoading) {
    return <LoadingState message="Loading dashboard..." />
  }

  if (error || !userRecord) {
    return <ErrorState title="User not found in database!" description="Please try signing in again." />
  }

  const handleIssueClick = (ticketId: string) => {
    navigate(`/issues/${ticketId}`)
  }

  const handleAddDevice = () => {
    setShowAddDeviceModal(true)
  }

  const handleSubmitDevice = async (deviceData: DeviceFormData) => {
    try {
      await createDeviceMutation.mutateAsync({
        deviceData: {
          name: deviceData.deviceName,
          os_type: deviceData.operatingSystem,
          device_type: deviceData.deviceType,
          mac_address: deviceData.macAddress,
          user_id: deviceData.deviceOwner || undefined,
          kaseya_group_id: deviceData.kaseyaGroupId || undefined,
          deployment_email: deviceData.deploymentEmail || undefined,
          deployment_os: deviceData.operatingSystem,
          deployment_arch: deviceData.architecture
        },
        companyId: deviceData.companyId!,
        sendAgentNow: deviceData.sendAgentNow
      })
      setShowAddDeviceModal(false)
    } catch (error) {
      console.error('Error creating device:', error)
    }
  }

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500">Monitor issues, devices, and organizations across the platform.</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <FilterDropdown
              filters={filters}
              onFilterChange={setFilters}
              organizations={organizationOptions}
              storageKey="admin-dashboard-filters"
            />

            <Select
              value={dateRangeFilter}
              onChange={(event) => setDateRangeFilter(event.target.value as DateRangeFilter)}
            >
              {dateRangeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>

            <button
              onClick={handleAddDevice}
              className="inline-flex items-center px-4 py-3 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Device
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white p-6">
          <ActiveIssuesChart tickets={filteredTickets} />
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white">
          <div className="flex flex-col gap-3 border-b border-gray-200 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Issues</h2>
              <p className="text-sm text-gray-500">{filteredTickets.length} issues match the current filters.</p>
            </div>
            <button
              onClick={() => setSortDescending(!sortDescending)}
              className="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              <ArrowUpDown className="mr-2 h-4 w-4" />
              {sortDescending ? 'Newest first' : 'Oldest first'}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Issue ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Submitted by</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Device Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date Submitted</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredTickets.map(ticket => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{ticket.id.slice(0, 8)}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{ticket.created_by_email || 'Unknown'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate">{ticket.title}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{ticket.company_name || '—'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                          ticket.priority === 'high'
                            ? 'bg-red-100 text-red-700'
                            : ticket.priority === 'medium'
                            ? 'bg-orange-100 text-orange-700'
                            : ticket.priority === 'low'
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                          ticket.device_status === 'online'
                            ? 'bg-green-100 text-green-700'
                            : ticket.device_status === 'offline'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <span
                          className={`mr-2 inline-block h-2 w-2 rounded-full ${
                            ticket.device_status === 'online'
                              ? 'bg-green-500'
                              : ticket.device_status === 'offline'
                              ? 'bg-red-500'
                              : 'bg-gray-400'
                          }`}
                        />
                        {ticket.device_status ? ticket.device_status.charAt(0).toUpperCase() + ticket.device_status.slice(1) : 'Unknown'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                      <button
                        onClick={() => handleIssueClick(ticket.id)}
                        className="text-gray-500 transition-colors hover:text-gray-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredTickets.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                      No issues match the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddDeviceModal
        isOpen={showAddDeviceModal}
        onClose={() => setShowAddDeviceModal(false)}
        onAddDevice={handleSubmitDevice}
      />
    </DashboardLayout>
  )
}